
<template>
  <div class="m-auto m-1230">
    <div class="row align-items-center">
      <div class="col-lg-6 d-none d-lg-block">
        <img src="@/assets/images/login.jpg" class="rounded-3" alt="login" />
      </div>
      <div class="col-lg-6">
        <div class="mw-480 ms-lg-auto">
          <h3 class="fs-28 mb-2">Bentornato!</h3>
          <p class="fw-medium fs-16 mb-4">
            Accedi per continuare
          </p>
          
          <form @submit.prevent="showVerificationStep ? handleVerification() : handleLogin()" class="auth-form">
            <!-- API Error Message -->
            <div v-if="apiError" class="alert alert-danger" role="alert">
              {{ apiError }}
            </div>
            <!-- Success Message for verification code sent -->
            <div v-if="verificationSent && showVerificationStep" class="alert alert-success" role="alert">
              Ti abbiamo inviato un codice di verifica all'indirizzo {{ email }}. Inseriscilo qui sotto per completare l'accesso.
            </div>

            <!-- Login Form Fields (hidden during verification) -->
            <div v-if="!showVerificationStep">
              <div class="form-floating mb-4">
                <input
                  type="email"
                  class="form-control h-55"
                  :class="{ 'is-invalid': emailError }"
                  id="loginEmail"
                  placeholder="esempio@email.com"
                  v-model="email"
                  @blur="validateEmail"
                  @input="validateEmail"
                  required
                />
                <label for="loginEmail">Indirizzo Email</label>
                <div v-if="emailError" class="invalid-feedback">
                  {{ emailError }}
                </div>
              </div>
              <div class="form-floating mb-4 position-relative">
                <input
                  :type="passwordFieldType"
                  class="form-control h-55"
                  id="loginPassword"
                  placeholder="Inserisci la password"
                  v-model="password"
                  required
                />
                <label for="loginPassword">Password</label>
                <button
                  type="button"
                  class="btn border-0 position-absolute top-50 end-0 translate-middle-y d-flex align-items-center justify-content-center"
                  @click="togglePasswordVisibility"
                >
                  <i class="material-symbols-outlined">
                    {{ isPasswordVisible ? 'visibility_off' : 'visibility' }}
                  </i>
                </button>
              </div>
              <div class="form-group mb-4">
                <a
                  href="#"
                  @click.prevent="$emit('switch-to-forgot')"
                  class="text-decoration-none text-primary fw-semibold"
                >
                  Password dimenticata?
                </a>
              </div>
            </div>

            <!-- Email Verification Step (shown when verification is needed) -->
            <div v-if="showVerificationStep">
              <div class="form-floating mb-4">
                <input
                  type="email"
                  class="form-control h-55"
                  id="verifyEmail"
                  placeholder="esempio@email.com"
                  v-model="email"
                  disabled
                />
                <label for="verifyEmail">Indirizzo Email</label>
              </div>
              <div class="form-floating mb-4">
                <input
                  type="text"
                  class="form-control h-55"
                  id="verificationCode"
                  placeholder="Inserisci il codice di verifica"
                  v-model="verificationCode"
                  maxlength="6"
                  required
                />
                <label for="verificationCode">Codice di Verifica</label>
              </div>
              <div class="form-group mb-4">
                <button
                  type="button"
                  class="btn btn-link p-0 text-decoration-none"
                  @click="resendCode"
                  :disabled="resendLoading"
                >
                  <div v-if="resendLoading" class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  Non hai ricevuto il codice? Invia di nuovo
                </button>
              </div>
            </div>
            <div class="form-group mb-4">
              <button
                type="submit"
                class="btn btn-primary fw-medium py-2 px-3 w-100"
                :disabled="(!showVerificationStep && (!isFormValid || isLoading)) || (showVerificationStep && isLoading)"
              >
                <div v-if="isLoading" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <div v-else class="d-flex align-items-center justify-content-center py-1">
                  <i class="material-symbols-outlined text-white fs-20 me-2">
                    {{ showVerificationStep ? 'verified_user' : 'login' }}
                  </i>
                  <span>{{ showVerificationStep ? 'Verifica Email' : 'Accedi' }}</span>
                </div>
              </button>
            </div>

            <div class="form-group">
              <p>
                Non hai un account?
                <a
                  href="#"
                  class="fw-medium text-primary text-decoration-none"
                  @click.prevent="$emit('switch-to-register')"
                >
                  Registrati
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { authStore } from "@/stores/auth";
import { formatErrorMessage } from "@/utils/errorUtils";

export default defineComponent({
  name: "AuthLogin",
  emits: ['login-success', 'switch-to-register', 'switch-to-forgot'],
  setup(props, { emit }) {
    const email = ref('');
    const password = ref('');
    const emailError = ref('');
    const apiError = ref('');
    const isLoading = ref(false);
    const showVerificationStep = ref(false);
    const verificationSent = ref(false);
    const verificationCode = ref('');
    const resendLoading = ref(false);
    const isPasswordVisible = ref(false);
    const passwordFieldType = ref('password');

    const togglePasswordVisibility = () => {
      isPasswordVisible.value = !isPasswordVisible.value;
      passwordFieldType.value = isPasswordVisible.value ? 'text' : 'password';
    };

    const validateEmail = () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email.value) {
        emailError.value = 'L\'email è obbligatoria';
        return false;
      }
      if (!emailRegex.test(email.value)) {
        emailError.value = 'Inserisci un indirizzo email valido';
        return false;
      }
      emailError.value = '';
      return true;
    };

    const isFormValid = computed(() => {
      return email.value && password.value && !emailError.value;
    });

    const handleLogin = async () => {
      if (!validateEmail()) {
        return;
      }

      isLoading.value = true;
      apiError.value = '';

      try {
        const result = await authStore.login({
          email: email.value,
          password: password.value
        });

        if (result.needsVerification) {
          showVerificationStep.value = true;
          // Send verification code automatically when user needs verification
          try {
            await authStore.resendVerification(email.value);
            verificationSent.value = true; // Set only if successful
          } catch (resendError) {
            console.error("Failed to send verification code:", resendError);
            apiError.value = formatErrorMessage(resendError);
            verificationSent.value = false;
          }
        } else {
          emit('login-success');
        }
      } catch (error: any) {
        console.error("Login failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        isLoading.value = false;
      }
    };

    const handleVerification = async () => {
      apiError.value = '';
      if (!verificationCode.value) {
        apiError.value = 'Inserisci il codice di verifica.';
        return;
      }

      isLoading.value = true;

      try {
        await authStore.verifyEmail({
          email: email.value,
          verification_code: verificationCode.value
        });
        emit('login-success');
      } catch (error: any) {
        console.error("Email verification failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        isLoading.value = false;
      }
    };

    const resendCode = async () => {
      resendLoading.value = true;
      apiError.value = '';

      try {
        await authStore.resendVerification(email.value);
        verificationSent.value = true;
        apiError.value = '';
        // Could show a success message here
      } catch (error: any) {
        verificationSent.value = false;
        console.error("Resend verification failed:", error);
        apiError.value = formatErrorMessage(error);
      } finally {
        resendLoading.value = false;
      }
    };


    return {
      email,
      password,
      emailError,
      apiError,
      isLoading,
      showVerificationStep,
      verificationSent,
      verificationCode,
      resendLoading,
      isPasswordVisible,
      passwordFieldType,
      togglePasswordVisibility,
      isFormValid,
      handleLogin,
      handleVerification,
      resendCode,
      validateEmail
    };
  },
});
</script>
